import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { useCrmMeta } from "../../hooks/useCrmMeta";
import StatusBadge from "../../components/StatusBadge";
import ActivityLogModal from "../../components/crm/ActivityLogModal";
import { Phone, Mail, Video, StickyNote, Link2, FileText, Trophy, X, ChevronRight } from "lucide-react";

const money = (v) =>
    `₱${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const s = (v, fallback = "—") => {
    if (v == null || v === "") return fallback;
    if (typeof v === "string" || typeof v === "number") return v;
    return fallback;
};

const TYPE_ICON = { Call: Phone, Email: Mail, Meeting: Video, Note: StickyNote };

export default function DealDetail() {
    const { id } = useParams();
    const { meta } = useCrmMeta();

    const [deal, setDeal] = useState(null);
    const [errored, setErrored] = useState(false);
    const [busy, setBusy] = useState(false);

    const [activities, setActivities] = useState([]);
    const [activitiesLoading, setActivitiesLoading] = useState(true);

    const [logOpen, setLogOpen] = useState(false);
    const [logType, setLogType] = useState("Call");

    const [closeAction, setCloseAction] = useState(null); // "won" | "lost" | null
    const [closeReason, setCloseReason] = useState("");
    const [closing, setClosing] = useState(false);

    const [linkOpen, setLinkOpen] = useState(false);

    const loadDeal = async () => {
        try {
            const { data } = await api.get(`/crm/deals/${id}`);
            setDeal(data && typeof data === "object" ? data : null);
        } catch (e) {
            setErrored(true);
            toast.error(formatApiError(e));
        }
    };

    const loadActivities = async () => {
        setActivitiesLoading(true);
        try {
            const { data } = await api.get("/crm/activities", { params: { relatedType: "deal", relatedId: id } });
            setActivities(Array.isArray(data) ? data : []);
        } catch (e) {
            setActivities([]);
        } finally {
            setActivitiesLoading(false);
        }
    };

    useEffect(() => {
        loadDeal();
        loadActivities();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const changeStage = async (stage) => {
        setBusy(true);
        try {
            const { data } = await api.put(`/crm/deals/${id}`, { stage });
            setDeal(data);
            toast.success("Stage updated");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const submitClose = async () => {
        setClosing(true);
        try {
            const { data } = await api.post(`/crm/deals/${id}/close`, { outcome: closeAction, reason: closeReason || undefined });
            setDeal(data);
            toast.success(closeAction === "won" ? "Deal marked as Won" : "Deal marked as Lost");
            setCloseAction(null);
            setCloseReason("");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setClosing(false);
        }
    };

    const openLog = (type) => {
        setLogType(type);
        setLogOpen(true);
    };

    if (errored) {
        return (
            <div className="p-3 md:p-6" data-testid="crm-deal-detail-page">
                <div className="sc-card p-8 text-center text-sm text-muted-foreground">Couldn't load this deal.</div>
            </div>
        );
    }

    if (!deal) {
        return (
            <div className="p-3 md:p-6" data-testid="crm-deal-detail-page">
                <div className="sc-card p-8 text-center text-sm text-muted-foreground">Loading…</div>
            </div>
        );
    }

    const isClosed = String(deal.stage || "").toLowerCase().includes("closed");
    const embeddedActivities = Array.isArray(deal.activities) ? deal.activities : null;
    const rows = activities.length > 0 ? activities : (embeddedActivities || []);

    return (
        <div className="p-3 md:p-6 space-y-5" data-testid="crm-deal-detail-page">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                <div>
                    <div className="sc-section-title">Deal {s(deal.record_no, "")}</div>
                    <h1 className="text-xl md:text-2xl font-semibold tracking-tight">{s(deal.deal_name)}</h1>
                    <div className="text-sm text-muted-foreground mt-1">
                        {s(deal.account_name)}{deal.contact_name ? ` · ${s(deal.contact_name)}` : ""}
                    </div>
                </div>
                <StatusBadge status={s(deal.stage, "open")} />
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <SummaryCard label="Amount" value={money(deal.amount)} />
                <SummaryCard label="Probability" value={deal.probability != null ? `${deal.probability}%` : "—"} />
                <SummaryCard label="Expected Revenue" value={deal.expected_revenue != null ? money(deal.expected_revenue) : "—"} />
                <SummaryCard label="Closing Date" value={deal.closing_date ? new Date(deal.closing_date).toLocaleDateString("en-PH", { timeZone: "Asia/Manila" }) : "—"} />
            </div>

            <div className="grid lg:grid-cols-3 gap-4">
                <div className="sc-card p-4 space-y-3 lg:col-span-2">
                    <div className="sc-section-title">Workflow</div>

                    <div className="flex flex-col sm:flex-row gap-2">
                        <select
                            className="sc-input flex-1"
                            value={deal.stage || ""}
                            disabled={busy || isClosed}
                            onChange={(e) => changeStage(e.target.value)}
                            data-testid="deal-stage-select"
                        >
                            {(meta.deal_stages || []).map((st) => (
                                <option key={st.stage} value={st.stage}>{st.stage}</option>
                            ))}
                        </select>

                        {!isClosed && (
                            <div className="flex gap-2">
                                <button
                                    onClick={() => setCloseAction("won")}
                                    className="h-9 px-3 rounded-md bg-emerald-600 text-white text-sm hover:opacity-90"
                                    data-testid="deal-close-won"
                                >
                                    Close Won
                                </button>
                                <button
                                    onClick={() => setCloseAction("lost")}
                                    className="h-9 px-3 rounded-md bg-red-600 text-white text-sm hover:opacity-90"
                                    data-testid="deal-close-lost"
                                >
                                    Close Lost
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="grid sm:grid-cols-2 gap-3 pt-2 border-t border-border">
                        <div className="rounded-md border border-border p-3">
                            <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Quotation</div>
                            <div className="flex items-center justify-between gap-2">
                                <span className="font-mono text-sm">{s(deal.quotation_no)}</span>
                                <button
                                    onClick={() => setLinkOpen(true)}
                                    className="h-7 px-2 inline-flex items-center gap-1 rounded border border-border text-xs hover:bg-secondary"
                                    data-testid="deal-link-quotation"
                                >
                                    <Link2 className="w-3.5 h-3.5" /> Link
                                </button>
                            </div>
                        </div>
                        <div className="rounded-md border border-border p-3">
                            <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Sales Order</div>
                            <div className="font-mono text-sm">{s(deal.sales_order_no)}</div>
                        </div>
                    </div>
                </div>

                <div className="sc-card p-4">
                    <div className="sc-section-title mb-2">Stage History</div>
                    <StageHistory history={deal.stage_history} />
                </div>
            </div>

            <div className="sc-card">
                <div className="px-4 py-3 border-b border-border flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <h2 className="text-sm font-medium tracking-tight">Activity Timeline</h2>
                    <div className="flex flex-wrap gap-2">
                        {["Call", "Email", "Meeting", "Note"].map((t) => {
                            const Icon = TYPE_ICON[t];
                            return (
                                <button
                                    key={t}
                                    onClick={() => openLog(t)}
                                    className="h-8 inline-flex items-center gap-1 px-2.5 rounded-md border border-border text-xs hover:bg-secondary"
                                    data-testid={`deal-log-${t.toLowerCase()}`}
                                >
                                    <Icon className="w-3.5 h-3.5" /> Log {t}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className="p-4">
                    <ActivityList rows={rows} loading={activitiesLoading} />
                </div>
            </div>

            {logOpen && (
                <ActivityLogModal
                    open={logOpen}
                    defaultType={logType}
                    related={{ type: "deal", id: deal.id, label: deal.deal_name }}
                    onClose={() => setLogOpen(false)}
                    onSaved={loadActivities}
                />
            )}

            {closeAction && (
                <div className="fixed inset-0 bg-black/40 grid place-items-center p-4 z-50" onClick={() => setCloseAction(null)}>
                    <div className="sc-card p-5 w-full max-w-md space-y-4 bg-background" onClick={(e) => e.stopPropagation()}>
                        <div>
                            <div className="text-xs uppercase tracking-wider text-muted-foreground">Close Deal</div>
                            <div className="text-lg font-semibold flex items-center gap-2">
                                {closeAction === "won" ? <Trophy className="w-4 h-4 text-emerald-600" /> : <X className="w-4 h-4 text-red-600" />}
                                Mark as {closeAction === "won" ? "Won" : "Lost"}
                            </div>
                        </div>
                        <label className="block space-y-1">
                            <div className="text-xs font-medium">Reason (optional)</div>
                            <textarea className="sc-input h-auto py-2" rows={3} value={closeReason} onChange={(e) => setCloseReason(e.target.value)} />
                        </label>
                        <div className="flex justify-end gap-2">
                            <button onClick={() => setCloseAction(null)} className="h-9 px-3 rounded border border-border text-sm" disabled={closing}>Cancel</button>
                            <button
                                onClick={submitClose}
                                disabled={closing}
                                className={`h-9 px-4 rounded text-white text-sm disabled:opacity-50 ${closeAction === "won" ? "bg-emerald-600" : "bg-red-600"}`}
                                data-testid="deal-close-confirm"
                            >
                                {closing ? "Saving…" : `Confirm ${closeAction === "won" ? "Won" : "Lost"}`}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {linkOpen && (
                <LinkQuotationModal
                    onClose={() => setLinkOpen(false)}
                    onLinked={async (q) => {
                        setLinkOpen(false);
                        try {
                            const { data } = await api.post(`/crm/deals/${id}/link-quotation`, { quotation_id: q.id });
                            setDeal(data);
                            toast.success(`Linked quotation ${s(q.doc_no)}`);
                        } catch (e) {
                            toast.error(formatApiError(e));
                        }
                    }}
                />
            )}
        </div>
    );
}

function StageHistory({ history }) {
    const list = Array.isArray(history) ? history : [];
    if (list.length === 0) return <div className="text-sm text-muted-foreground">No stage changes yet.</div>;

    return (
        <ul className="space-y-2">
            {list.map((h, i) => {
                if (h == null) return null;
                if (typeof h === "string") {
                    return <li key={i} className="text-sm text-muted-foreground">{h}</li>;
                }
                const stage = s(h.stage || h.to_stage);
                const from = s(h.from_stage, "");
                const at = h.at || h.changed_at || h.timestamp;
                const by = h.by || h.changed_by || h.owner_email;
                return (
                    <li key={i} className="text-xs flex flex-col gap-0.5 border-b border-border/60 pb-2 last:border-0">
                        <div className="flex items-center gap-1 font-medium">
                            {from && from !== "—" ? (
                                <>
                                    <span>{from}</span>
                                    <ChevronRight className="w-3 h-3 text-muted-foreground" />
                                </>
                            ) : null}
                            <span>{stage}</span>
                        </div>
                        <div className="text-muted-foreground font-mono">{at ? new Date(at).toLocaleString("en-PH", { timeZone: "Asia/Manila" }) : "—"}</div>
                        {by && <div className="text-muted-foreground">by {s(by)}</div>}
                    </li>
                );
            })}
        </ul>
    );
}

function ActivityList({ rows, loading }) {
    const [expanded, setExpanded] = useState(() => new Set());

    const toggle = (id) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    if (loading) return <div className="text-sm text-muted-foreground text-center py-6">Loading activity…</div>;
    if (!rows || rows.length === 0) return <div className="text-sm text-muted-foreground text-center py-6">No activity logged yet.</div>;

    return (
        <ul className="space-y-2">
            {rows.map((a) => {
                const Icon = TYPE_ICON[a.type] || FileText;
                const isOpen = expanded.has(a.id);
                const hasPresented = (a.presented_quotation_nos || []).length > 0 || (a.presented_items || []).length > 0 || a.presented_url || a.presented_notes;

                return (
                    <li key={a.id} className="border border-border rounded-md p-3" data-testid={`activity-row-${a.id}`}>
                        <div className="flex items-start justify-between gap-3">
                            <div className="flex items-start gap-2 min-w-0">
                                <Icon className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" />
                                <div className="min-w-0">
                                    <div className="text-sm font-medium truncate">{s(a.subject)}</div>
                                    <div className="text-xs text-muted-foreground">
                                        {s(a.type)} · {a.direction ? `${s(a.direction)} · ` : ""}{a.occurred_at ? new Date(a.occurred_at).toLocaleString("en-PH", { timeZone: "Asia/Manila" }) : "—"}
                                        {a.owner_name ? ` · ${s(a.owner_name)}` : ""}
                                    </div>
                                </div>
                            </div>
                            {a.outcome && <span className="sc-pill bg-secondary border border-border shrink-0">{s(a.outcome)}</span>}
                        </div>

                        {(a.pitch || hasPresented) && (
                            <button
                                onClick={() => toggle(a.id)}
                                className="text-xs text-muted-foreground underline underline-offset-2 mt-2"
                            >
                                {isOpen ? "Hide details" : "Show pitch / presented"}
                            </button>
                        )}

                        {isOpen && (
                            <div className="mt-2 text-xs space-y-2 bg-secondary/30 rounded-md p-3">
                                {a.pitch && (
                                    <div>
                                        <div className="font-medium text-muted-foreground uppercase tracking-wider text-[10px] mb-1">Pitch</div>
                                        <div className="whitespace-pre-wrap">{s(a.pitch)}</div>
                                    </div>
                                )}
                                {(a.presented_quotation_nos || []).length > 0 && (
                                    <div>
                                        <div className="font-medium text-muted-foreground uppercase tracking-wider text-[10px] mb-1">Quotations Presented</div>
                                        <div className="flex flex-wrap gap-1">
                                            {a.presented_quotation_nos.map((no, i) => (
                                                <span key={i} className="sc-pill bg-background border border-border font-mono">{s(no)}</span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {(a.presented_items || []).length > 0 && (
                                    <div>
                                        <div className="font-medium text-muted-foreground uppercase tracking-wider text-[10px] mb-1">Items Presented</div>
                                        <div className="flex flex-wrap gap-1">
                                            {a.presented_items.map((it, i) => (
                                                <span key={i} className="sc-pill bg-background border border-border">
                                                    <span className="font-mono">{s(it?.item_code)}</span>
                                                    {it?.item_name ? ` · ${s(it.item_name)}` : ""}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {a.presented_url && (
                                    <div>
                                        <a href={a.presented_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline break-all">{s(a.presented_url)}</a>
                                    </div>
                                )}
                                {a.presented_notes && <div className="whitespace-pre-wrap">{s(a.presented_notes)}</div>}
                            </div>
                        )}
                    </li>
                );
            })}
        </ul>
    );
}

function LinkQuotationModal({ onClose, onLinked }) {
    const [query, setQuery] = useState("");
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/quotations");
                setOptions(Array.isArray(data) ? data : []);
            } catch (e) {
                setOptions([]);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const filtered = options.filter((q) => {
        const query_ = query.trim().toLowerCase();
        if (!query_) return true;
        return String(q.doc_no || "").toLowerCase().includes(query_) || String(q.customer_name || "").toLowerCase().includes(query_);
    });

    return (
        <div className="fixed inset-0 bg-black/40 grid place-items-center p-4 z-50" onClick={onClose}>
            <div className="sc-card p-5 w-full max-w-lg space-y-3 bg-background max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">Link Quotation</div>
                    <div className="text-lg font-semibold">Select a quotation</div>
                </div>
                <input
                    autoFocus
                    className="sc-input"
                    placeholder="Search doc no / customer…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                />
                <div className="border border-border rounded-md divide-y divide-border">
                    {loading && <div className="p-4 text-sm text-muted-foreground text-center">Loading…</div>}
                    {!loading && filtered.length === 0 && <div className="p-4 text-sm text-muted-foreground text-center">No quotations found.</div>}
                    {!loading && filtered.slice(0, 30).map((q) => (
                        <button
                            key={q.id}
                            onClick={() => onLinked(q)}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-secondary flex items-center justify-between gap-2"
                        >
                            <span className="font-mono text-xs">{q.doc_no}</span>
                            <span className="text-muted-foreground text-xs truncate">{s(q.customer_name)}</span>
                        </button>
                    ))}
                </div>
                <div className="flex justify-end">
                    <button onClick={onClose} className="h-9 px-3 rounded border border-border text-sm">Cancel</button>
                </div>
            </div>
        </div>
    );
}

function SummaryCard({ label, value }) {
    return (
        <div className="sc-card p-4">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className="text-lg font-semibold mt-1">{value}</div>
        </div>
    );
}
